import { request } from "./client.js";
import { E } from "./endpoints.js";
import type { LkLoginResponse, TotpSetupResponse } from "../types/auth.js";
import {
  lkLoginResponseSchema,
  totpSetupResponseSchema,
} from "../schemas/auth.js";

/** POST /v1/lk/auth/telegram — multipart/form-data from TG widget (legacy widget login) */
export async function postTelegramLogin(
  formData: FormData,
): Promise<LkLoginResponse> {
  return request(
    E.lk.authTelegram,
    { method: "POST", form: formData },
    lkLoginResponseSchema,
  );
}

/** POST /v1/lk/auth/logout */
export async function logout(): Promise<void> {
  return request(E.lk.authLogout, { method: "POST" });
}

// ── auth-v2 (2026-05-12): bot deeplink login ─────────────────────────────────

export interface BotDeeplinkStartResponse {
  code: string;
  deeplink_url: string;
  web_fallback_url: string;
}

export interface BotDeeplinkPollResponse {
  status: "pending" | "ok" | "expired";
  user_id?: string;
  totp_required?: boolean;
  totp_enabled?: boolean;
}

/** POST /v1/lk/auth/bot-deeplink/start — issue a one-shot login code + tg:// deeplink. */
export async function botDeeplinkStart(): Promise<BotDeeplinkStartResponse> {
  return request<BotDeeplinkStartResponse>(E.lk.botDeeplinkStart, {
    method: "POST",
  });
}

/** GET /v1/lk/auth/bot-deeplink/poll/{code} — poll until ok|expired; sets cookie on ok. */
export async function botDeeplinkPoll(
  code: string,
): Promise<BotDeeplinkPollResponse> {
  return request<BotDeeplinkPollResponse>(E.lk.botDeeplinkPoll(code));
}

// ── LK TOTP (2026-05-12) ─────────────────────────────────────────────────────

/** POST /v1/lk/auth/totp/setup */
export async function totpSetup(): Promise<TotpSetupResponse> {
  return request(E.lk.totpSetup, { method: "POST" }, totpSetupResponseSchema);
}

export interface TotpVerifyResponse {
  ok: boolean;
  used_backup_code?: boolean;
  backup_codes_remaining?: number;
  /**
   * Present exactly once: in the response of the very first /totp/verify
   * call that flips totp_enabled from false to true. SPA must surface these
   * to the user — the server never returns plaintext codes again.
   */
  backup_codes?: string[];
}

/** POST /v1/lk/auth/totp/verify — json body { code } */
export async function totpVerify(code: string): Promise<TotpVerifyResponse> {
  return request(E.lk.totpVerify, { method: "POST", body: { code } });
}

/** Re-mint a fresh batch of 10 LK backup codes (invalidates the previous set). */
export async function totpRegenerateBackupCodes(): Promise<{
  backup_codes: string[];
  count: number;
}> {
  return request(E.lk.totpBackupRegenerate, { method: "POST" });
}

/** POST /v1/lk/auth/totp/disable — requires session.totp_verified */
export async function totpDisable(): Promise<{ ok: boolean }> {
  return request(E.lk.totpDisable, { method: "POST" });
}

// ── Bot-session exchange (2026-05-12): one-shot token from /start <invite> ─

export interface BotSessionExchangeResponse {
  ok: boolean;
  user_id: string;
  totp_required: boolean;
  totp_enabled: boolean;
}

/** POST /v1/lk/auth/bot-session-exchange — burn one-shot token, set cookie. */
export async function botSessionExchange(
  token: string,
): Promise<BotSessionExchangeResponse> {
  return request<BotSessionExchangeResponse>(E.lk.botSessionExchange, {
    method: "POST",
    body: { token },
  });
}
